import { RotateCcw, Clock, CreditCard, AlertTriangle, Shield, DollarSign, CheckCircle, XCircle, Phone, Mail } from "lucide-react";
import { motion } from "framer-motion";

const ReturnPolicy = () => {
  const returnRules = [
    { icon: <XCircle className="w-5 h-5" />, title: "No Returns After Trip Starts", description: "Once the trip begins, cancellations will not be accepted, and the remaining amount must be paid." },
    { icon: <Clock className="w-5 h-5" />, title: "Pre-Trip Cancellation", description: "Cancellations made before the trip starts are eligible for a refund, provided the request is made at least 2 hours in advance." },
    { icon: <CreditCard className="w-5 h-5" />, title: "Processing Timeline", description: "Refunds for eligible cancellations will be processed within 5-7 business days." },
    { icon: <AlertTriangle className="w-5 h-5" />, title: "No-Show Policy", description: "In case of a no-show or failure to collect the bike, no refund will be provided." },
    { icon: <XCircle className="w-5 h-5" />, title: "Early Returns", description: "If the bike is returned earlier than the scheduled time, no partial refund will be given." },
    { icon: <Shield className="w-5 h-5" />, title: "Damage Deductions", description: "Any damages to the bike during the rental period will be deducted from the security deposit." },
    { icon: <DollarSign className="w-5 h-5" />, title: "Original Payment Method", description: "Refunds will only be processed to the original payment method." },
    { icon: <AlertTriangle className="w-5 h-5" />, title: "Policy Discretion", description: "The company reserves the right to refuse refunds for reasons outside its policy." },
  ];

  const returnProcess = [
    { step: "1", title: "Inspect the Bike", description: "Before returning, check the bike for any damages. Document any issues with photos.", icon: "🔍" },
    { step: "2", title: "Return on Time", description: "Return the bike to the agreed location at or before the scheduled return time.", icon: "⏰" },
    { step: "3", title: "Staff Inspection", description: "Our team will inspect the bike for damages, fuel level, and overall condition.", icon: "✓" },
    { step: "4", title: "Deposit Refund", description: "Security deposit will be refunded after deducting any applicable charges within 3-5 days.", icon: "💰" },
  ];

  const scenarios = [
    { scenario: "On-Time Return", description: "Bike returned on time with no damages", outcome: "Full security deposit refunded within 3-5 business days", positive: true },
    { scenario: "Early Return", description: "Bike returned before scheduled time", outcome: "No partial refund for unused rental time", positive: false },
    { scenario: "Late Return", description: "Bike returned after scheduled time", outcome: "Late fees apply: ₹200 for first 30 mins, then hourly rates", positive: false },
    { scenario: "Damaged Bike", description: "Bike returned with damages", outcome: "Repair costs deducted from security deposit", positive: false },
  ];

  const container = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };
  const cardItem = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero */}
      <div className="relative bg-[#111111] border-b border-white/[0.08] py-16 sm:py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`, backgroundSize: "50px 50px" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF6B35] rounded-full filter blur-[120px] opacity-5 pointer-events-none" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative max-w-4xl mx-auto text-center">
          <div className="w-14 h-14 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <RotateCcw className="w-7 h-7 text-[#FF6B35]" />
          </div>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-xs font-bold uppercase tracking-widest mb-4">Policy</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">Return Policy</h1>
          <p className="text-base sm:text-lg text-gray-400">Clear guidelines for returning your rental vehicle at OkMechanic</p>
        </motion.div>
      </div>

      {/* Rules Grid */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white text-center mb-2">Return Rules & Conditions</h2>
        <p className="text-center text-gray-500 text-sm mb-6">Understand all terms before completing your rental</p>
        <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-50px" }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {returnRules.map((rule, i) => (
            <motion.div key={i} variants={cardItem}
              className="bg-[#111111] border border-white/[0.08] hover:border-[#FF6B35]/20 rounded-2xl p-4 transition-colors">
              <div className="w-9 h-9 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-xl flex items-center justify-center mb-3 text-[#FF6B35]">
                {rule.icon}
              </div>
              <h3 className="text-sm font-bold text-white mb-2">{rule.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{rule.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Return Process */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white text-center mb-8">Return Process</h2>
        <div className="grid md:grid-cols-4 gap-4">
          {returnProcess.map((step, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="bg-[#111111] border border-white/[0.08] rounded-2xl p-5 text-center">
              <div className="w-12 h-12 bg-[#FF6B35] rounded-2xl flex items-center justify-center mx-auto mb-3 text-white font-black text-lg shadow-lg shadow-[#FF6B35]/20">
                {step.step}
              </div>
              <div className="text-2xl mb-2">{step.icon}</div>
              <h3 className="font-bold text-white text-sm mb-2">{step.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Scenarios */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-black text-white text-center mb-6">Common Return Scenarios</h2>
        <div className="grid md:grid-cols-2 gap-3">
          {scenarios.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className={`bg-[#111111] border rounded-2xl p-5 ${s.positive ? "border-green-500/20" : "border-red-500/20"}`}>
              <h3 className="font-bold text-white text-sm mb-1">{s.scenario}</h3>
              <p className="text-xs text-gray-500 mb-2">{s.description}</p>
              <p className={`text-xs font-semibold ${s.positive ? "text-green-400" : "text-red-400"}`}>{s.outcome}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="max-w-5xl mx-auto px-4 py-8 pb-16">
        <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl p-6">
          <h2 className="text-lg font-bold text-white text-center mb-6">Questions About Returns?</h2>
          <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto">
            <a href="tel:9921426002" className="flex items-center gap-3 p-4 bg-white/[0.04] border border-white/[0.08] hover:border-[#FF6B35]/20 text-gray-300 rounded-xl transition-colors">
              <Phone className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
              <div><div className="text-xs text-gray-500">Call Us</div><div className="font-semibold text-sm">9921426002</div></div>
            </a>
            <a href="mailto:vegobike00@gmail.com" className="flex items-center gap-3 p-4 bg-white/[0.04] border border-white/[0.08] hover:border-[#FF6B35]/20 text-gray-300 rounded-xl transition-colors">
              <Mail className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
              <div><div className="text-xs text-gray-500">Email Us</div><div className="font-semibold text-sm break-all">vegobike00@gmail.com</div></div>
            </a>
          </div>
        </div>
        <p className="text-center text-xs text-gray-600 mt-6">Last Updated: October 2025 · © 2025 OkMechanic. All rights reserved.</p>
      </div>
    </div>
  );
};

export default ReturnPolicy;
